import { useComputed, useSignal } from "@preact/signals";
import type { JSX } from "preact";
import { useCallback, useEffect, useRef } from "preact/hooks";
import {
  getCachedMembers,
  type MentionSuggestion,
} from "../../utils/member-cache";
import { encodeEveryoneMention, encodeMention } from "../../utils/mentions";
import { MentionDropdown } from "./mention-dropdown";

type MentionInputProps = Omit<
  JSX.IntrinsicElements["textarea"],
  "class" | "autofocus" | "onInput" | "value" | "onKeyDown"
> & {
  class?: string;
  wrapperClass?: string;
  autoGrow?: boolean;
  autofocus?: boolean;
  value: string;
  workspaceId: string;
  onValueChange: (value: string) => void;
  onKeyDown?: (e: JSX.TargetedKeyboardEvent<HTMLTextAreaElement>) => void;
};

export function MentionInput({
  autoGrow = true,
  autofocus,
  class: className,
  wrapperClass,
  value,
  workspaceId,
  onValueChange,
  onKeyDown: onKeyDownProp,
  placeholder,
  ...props
}: MentionInputProps) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // State for mention autocomplete
  const isOpen = useSignal(false);
  const query = useSignal("");
  const mentionStart = useSignal<number | null>(null);
  const suggestions = useSignal<MentionSuggestion[]>([]);
  const loading = useSignal(false);
  const selectedIndex = useSignal(0);
  const includeEveryone = useSignal(false);

  // Combined list of suggestions including @everyone if applicable
  const allSuggestions = useComputed(() => {
    const result: MentionSuggestion[] = [];
    if (includeEveryone.value) {
      result.push({
        memberId: "everyone",
        name: "everyone",
        profilePictureUrl: null,
        isEveryone: true,
      });
    }
    return [...result, ...suggestions.value];
  });

  // Autofocus on mount
  useEffect(() => {
    if (autofocus) {
      ref.current?.focus();
    }
  }, [autofocus]);

  // Cached member list for client-side filtering
  const allMembers = useSignal<MentionSuggestion[]>([]);
  const pendingSelect = useSignal(false);

  // Fetch members when dropdown opens, using shared cache
  useEffect(() => {
    if (!isOpen.value) return;

    const result = getCachedMembers(workspaceId);
    if (Array.isArray(result)) {
      allMembers.value = result;
      loading.value = false;
    } else {
      loading.value = true;
      result.then((members) => {
        allMembers.value = members;
        loading.value = false;
      });
    }

    return () => {
      loading.value = false;
    };
  }, [isOpen.value, workspaceId]);

  // Filter cached members locally by query
  useEffect(() => {
    if (!isOpen.value) {
      suggestions.value = [];
      includeEveryone.value = false;
      return;
    }

    const q = query.value.toLowerCase();
    if (q.length === 0) {
      suggestions.value = allMembers.value;
    } else {
      suggestions.value = allMembers.value.filter((m) =>
        m.name.toLowerCase().startsWith(q),
      );
    }

    includeEveryone.value =
      q.length === 0 || "everyone".startsWith(q) || "all".startsWith(q);

    selectedIndex.value = 0;
  }, [query.value, isOpen.value, allMembers.value]);

  // insertMention is defined below and needs to be hoisted here via useCallback
  // to allow proper dependency tracking in the auto-select useEffect
  const insertMention = useCallback(
    (suggestion: MentionSuggestion) => {
      if (mentionStart.value === null || !ref.current) return;

      const beforeMention = value.slice(0, mentionStart.value);
      const afterMention = value.slice(ref.current.selectionStart);

      const mentionText = suggestion.isEveryone
        ? encodeEveryoneMention()
        : encodeMention(suggestion.name, suggestion.memberId);

      const newValue = `${beforeMention}${mentionText} ${afterMention}`;
      onValueChange(newValue);

      // Close dropdown
      isOpen.value = false;
      mentionStart.value = null;
      query.value = "";

      // Move cursor after the inserted mention
      const newCursorPos = beforeMention.length + mentionText.length + 1;
      requestAnimationFrame(() => {
        ref.current?.setSelectionRange(newCursorPos, newCursorPos);
        ref.current?.focus();
      });
    },
    [value, onValueChange],
  );

  // Auto-select first match when results arrive after pending Enter
  useEffect(() => {
    if (
      pendingSelect.value &&
      !loading.value &&
      allSuggestions.value.length > 0
    ) {
      pendingSelect.value = false;
      const first = allSuggestions.value[0];
      if (first) insertMention(first);
    }
  }, [loading.value, allSuggestions.value, insertMention]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen.value) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        ref.current &&
        !ref.current.contains(e.target as Node)
      ) {
        isOpen.value = false;
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen.value]);

  const handleInput = useCallback(
    (e: JSX.TargetedEvent<HTMLTextAreaElement>) => {
      const textarea = e.currentTarget;
      const newValue = textarea.value;
      const cursorPos = textarea.selectionStart;

      onValueChange(newValue);

      // Check if we should trigger mention autocomplete
      // Look backwards from cursor for @ that starts a mention
      const textBeforeCursor = newValue.slice(0, cursorPos);
      const atIndex = textBeforeCursor.lastIndexOf("@");

      if (atIndex !== -1) {
        const textAfterAt = textBeforeCursor.slice(atIndex + 1);
        // Valid mention: no spaces in the query, and @ is at start or after whitespace
        const charBeforeAt = atIndex > 0 ? (newValue[atIndex - 1] ?? " ") : " ";
        const isValidStart = /\s/.test(charBeforeAt) || atIndex === 0;

        if (isValidStart && !/\s/.test(textAfterAt)) {
          mentionStart.value = atIndex;
          query.value = textAfterAt;
          isOpen.value = true;
          return;
        }
      }

      // No valid mention trigger found
      isOpen.value = false;
      mentionStart.value = null;
      query.value = "";
    },
    [onValueChange],
  );

  const handleKeyDown = useCallback(
    (e: JSX.TargetedKeyboardEvent<HTMLTextAreaElement>) => {
      // If dropdown is open and has suggestions, handle navigation
      if (isOpen.value && allSuggestions.value.length > 0) {
        switch (e.key) {
          case "ArrowDown":
            e.preventDefault();
            selectedIndex.value =
              (selectedIndex.value + 1) % allSuggestions.value.length;
            return;
          case "ArrowUp":
            e.preventDefault();
            selectedIndex.value =
              (selectedIndex.value - 1 + allSuggestions.value.length) %
              allSuggestions.value.length;
            return;
          case "Enter":
            e.preventDefault();
            if (loading.value) {
              pendingSelect.value = true;
            } else {
              const selected = allSuggestions.value[selectedIndex.value];
              if (selected) {
                insertMention(selected);
              }
            }
            return;
          case "Tab":
            // Tab closes dropdown without selecting, allows normal tab navigation
            isOpen.value = false;
            return;
          case "Escape":
            e.preventDefault();
            isOpen.value = false;
            return;
        }
      }

      // If we didn't handle the event, pass to parent handler
      onKeyDownProp?.(e);
    },
    [insertMention, onKeyDownProp],
  );

  const displayValue = value || placeholder || "";

  const textareaElement = (
    <textarea
      ref={ref}
      class={autoGrow ? `autosize-textarea ${className ?? ""}` : className}
      value={value}
      placeholder={placeholder}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      aria-autocomplete="list"
      aria-expanded={isOpen.value}
      aria-controls={isOpen.value ? "mention-listbox" : undefined}
      {...props}
    />
  );

  const dropdown = isOpen.value && (
    <MentionDropdown
      dropdownRef={dropdownRef}
      suggestions={allSuggestions.value}
      selectedIndex={selectedIndex.value}
      loading={loading.value}
      onSelect={insertMention}
      onHover={(index) => {
        selectedIndex.value = index;
      }}
      class="absolute left-0 right-0 mt-1"
    />
  );

  if (!autoGrow) {
    return (
      <div class={`relative ${wrapperClass ?? ""}`}>
        {textareaElement}
        {dropdown}
      </div>
    );
  }

  return (
    <div class={`relative ${wrapperClass ?? ""}`}>
      <div class="autosize">
        {textareaElement}
        <pre class={`autosize-pre ${className ?? ""}`}>
          {displayValue}
          <br />
        </pre>
      </div>
      {dropdown}
    </div>
  );
}
